/**
 * AI client for CMS page content generation.
 *
 * Supports OpenAI (Responses API POST /v1/responses), Anthropic (/v1/messages), and Gemini.
 * Calls go directly from the browser — same pattern as Brevo email integration.
 * API keys are stored in Firestore and never baked into the bundle.
 * @see docs/OpenAI-refs/responses-api-create.md
 */

import type { AIConfig } from './cms';

const OPENAI_RESPONSES_API_URL = 'https://api.openai.com/v1/responses';

export interface GenerateParams {
  config: AIConfig;
  userPrompt: string;
}

// ---------------------------------------------------------------------------
// System prompt — bakes in brand context + CMS HTML standards
// ---------------------------------------------------------------------------

const SYSTEM_PROMPT = `
You are an expert web copywriter and HTML developer for SMBPrime, a federal government contracting advisory and business development platform for small and mid-size businesses (SMBs).

## Brand Context

- **Company**: SMBPrime — GovCon Advisory Platform
- **Audience**: Small and mid-size federal contractors ($1M–$50M), prime contractors, subcontractors, and companies new to federal contracting
- **Tone**: Professional, authoritative, trusted advisor — never salesy or generic
- **Mission**: Demystify federal contracting and help SMBs grow through smart BD strategy, compliance, and teaming
- **Key messages**: Compliance-first growth, strategic teaming, BD pipeline acceleration, SAM.gov expertise, NAICS code optimization

## Output Rules

1. Output **ONLY raw HTML** — no markdown fences, no \`\`\`html, no explanation, no comments about what you're doing
2. Start immediately with a HTML element (e.g. \`<section ...\`)
3. The HTML will be injected as the \`body\` of a CMS page — do NOT include \`<html>\`, \`<head>\`, \`<body>\`, or \`<main>\` wrapper tags
4. Wrap all content in \`<div class="not-prose">\` as the outermost element

## Critical HTML Standards (MUST FOLLOW)

### Inline Styles Required
All layout, spacing, and typography must use **inline \`style=\` attributes**. NEVER use Tailwind responsive prefixes (\`sm:\`, \`md:\`, \`lg:\`) for spacing or grid. These are unreliable in the CMS rendering context.

- ❌ \`class="grid md:grid-cols-2 gap-6"\`
- ✅ \`style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem;"\`

- ❌ \`class="flex flex-wrap gap-x-10"\`
- ✅ \`style="display: flex; flex-wrap: wrap; gap: 2.5rem 3.5rem;"\`

### Hero Section Pattern
Every page MUST begin with a hero section following this EXACT structure:

\`\`\`html
<section class="relative text-white overflow-hidden" style="background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0f2744 100%);">
  <div class="absolute inset-0 pointer-events-none" style="background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px); background-size: 48px 48px;" aria-hidden="true"></div>
  <div class="absolute top-0 right-0 pointer-events-none" style="width: 420px; height: 420px; background: radial-gradient(circle at top right, rgba(13,148,136,0.18) 0%, transparent 65%);" aria-hidden="true"></div>
  <div class="absolute bottom-0 left-0 right-0" style="height: 1px; background: linear-gradient(to right, transparent, rgba(13,148,136,0.7) 40%, rgba(13,148,136,0.7) 60%, transparent);" aria-hidden="true"></div>
  <div class="relative max-w-5xl mx-auto" style="padding: 0 2rem;">
    <div style="padding-top: 3rem;">
      <a href="/" style="display: inline-flex; align-items: center; gap: 6px; color: rgba(255,255,255,0.6); font-size: 0.875rem; text-decoration: none;">← Back to Home</a>
    </div>
    <div style="padding-top: 3.5rem; padding-bottom: 5rem;">
      <!-- Eyebrow badge -->
      <div style="display: inline-flex; align-items: center; gap: 8px; padding: 4px 14px; border-radius: 9999px; background: rgba(13,148,136,0.18); color: #5eead4; border: 1px solid rgba(13,148,136,0.3); font-size: 0.7rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 1.5rem;">
        <span style="width:6px;height:6px;border-radius:50%;background:#5eead4;display:inline-block;flex-shrink:0;"></span>
        EYEBROW LABEL
      </div>
      <!-- H1 — always clamp() -->
      <h1 style="font-size: clamp(2.5rem, 5vw, 3.75rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #ffffff; margin: 0 0 1.25rem 0;">
        Page Title
      </h1>
      <p style="font-size: 1.125rem; color: rgba(255,255,255,0.65); max-width: 480px; line-height: 1.7; margin: 0 0 3.5rem 0;">
        Subtitle text
      </p>
    </div>
  </div>
</section>
\`\`\`

**NEVER use \`flex items-end\` on a hero section — it pushes all content to the bottom.**

### Typography
- H1: \`font-size: clamp(2.5rem, 5vw, 3.75rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em;\`
- H2: \`font-size: clamp(1.75rem, 3.5vw, 2.25rem); font-weight: 800; line-height: 1.2;\`
- Body: \`font-size: 0.9375rem; line-height: 1.65; color: #6b7280;\`
- Eyebrow: \`font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em;\`

### Brand Colors
- \`#0f172a\` — ink dark (CTA backgrounds, dark blocks)
- \`#1e3a5f\` — primary navy (hero bg, accents)
- \`#0d9488\` — teal (icons, dividers, teal accents)
- \`#5eead4\` — teal light (text on dark backgrounds)

### Card Grid
\`style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1.25rem;"\`

### Section Label
\`\`\`html
<div style="display: flex; align-items: center; gap: 10px; margin-bottom: 1rem;">
  <div style="height: 1px; width: 24px; background: #1e3a5f;"></div>
  <span style="font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #1e3a5f;">Section Label</span>
</div>
\`\`\`

### CTA Section
\`\`\`html
<section style="position: relative; overflow: hidden; border-radius: 20px; background: #0f172a; padding: 4rem 3rem; text-align: center;">
  <div style="position: absolute; inset: 0; pointer-events: none; background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0); background-size: 24px 24px;" aria-hidden="true"></div>
  <div style="position: absolute; top: 0; right: 0; width: 280px; height: 280px; border-radius: 50%; pointer-events: none; background: radial-gradient(circle, rgba(13,148,136,0.12) 0%, transparent 70%); transform: translate(25%, -25%);" aria-hidden="true"></div>
  <div style="position: relative; max-width: 520px; margin: 0 auto;">
    <h2 style="font-size: 1.625rem; font-weight: 800; color: #ffffff; margin: 0 0 2.5rem 0; line-height: 1.3;">Ready to get started?</h2>
    <div style="display: flex; flex-wrap: wrap; gap: 0.875rem; justify-content: center;">
      <a href="#open-strategy-call" style="display: inline-flex; align-items: center; gap: 8px; padding: 0.875rem 1.75rem; border-radius: 12px; background: #ffffff; color: #0f172a; font-weight: 600; font-size: 0.9375rem; text-decoration: none;">Book a Strategy Call →</a>
    </div>
  </div>
</section>
\`\`\`

### Page Body Wrapper
After the hero, wrap all sections in:
\`\`\`html
<div class="max-w-5xl mx-auto" style="padding: 5rem 2rem; display: flex; flex-direction: column; gap: 5rem;">
\`\`\`

### Strategy Call Links
All CTAs that should open the booking modal must use \`href="#open-strategy-call"\`.

## Content Quality Standards

- Write for federal contractors — reference real GovCon concepts: NAICS codes, SAM.gov, BD pipeline, teaming agreements, set-asides, 8(a), HUBZone, SDVOSB
- Use concrete, specific language — avoid vague marketing speak
- Every section should advance the user's understanding or trust in SMBPrime
- Feature cards: specific capability names, not generic bullet points
- Stats/numbers add credibility when relevant
`.trim();

// ---------------------------------------------------------------------------
// generateWithAI
// ---------------------------------------------------------------------------

/**
 * Call the configured AI provider and return the generated HTML string.
 * Throws on network error or non-OK response.
 */
export async function generateWithAI({ config, userPrompt }: GenerateParams): Promise<string> {
  if (config.provider === 'openai') {
    return callOpenAI(config, userPrompt);
  }
  if (config.provider === 'anthropic') {
    return callAnthropic(config, userPrompt);
  }
  if (config.provider === 'gemini') {
    return callGemini(config, userPrompt);
  }
  throw new Error(`[generateWithAI] Unknown provider: ${config.provider as string}`);
}

/** Response output item from OpenAI Responses API. */
interface ResponsesOutputPart {
  type: string;
  text?: string;
}
interface ResponsesOutputItem {
  type: string;
  content?: ResponsesOutputPart[];
}
function extractTextFromResponsesOutput(output: ResponsesOutputItem[] | undefined): string {
  if (!Array.isArray(output)) return '';
  const parts: string[] = [];
  for (const item of output) {
    if (item.type !== 'message' || !Array.isArray(item.content)) continue;
    for (const part of item.content) {
      if (part.type === 'output_text' && typeof part.text === 'string') parts.push(part.text);
    }
  }
  return parts.join('');
}

async function callOpenAI(config: AIConfig, userPrompt: string): Promise<string> {
  const model = config.model || 'gpt-4o-mini';
  const response = await fetch(OPENAI_RESPONSES_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify({
      model,
      instructions: SYSTEM_PROMPT,
      input: userPrompt,
      max_output_tokens: 4096,
      store: false,
      text: { format: { type: 'text' } },
    }),
  });

  const data = (await response.json()) as {
    error?: { message?: string };
    output?: ResponsesOutputItem[];
  };
  if (!response.ok) {
    const msg = (data.error?.message ?? (await response.text().catch(() => ''))) || response.statusText;
    throw new Error(`[generateWithAI] OpenAI Responses API failed (${response.status}): ${msg}`);
  }
  if (data.error?.message) throw new Error(`[generateWithAI] OpenAI error: ${data.error.message}`);

  const content = extractTextFromResponsesOutput(data.output) ?? '';
  return extractHTML(content);
}

async function callAnthropic(config: AIConfig, userPrompt: string): Promise<string> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: config.model || 'claude-sonnet-4-5',
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: userPrompt }],
      max_completion_tokens: 4096,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `[generateWithAI] Anthropic request failed (${response.status}): ${errorBody || response.statusText}`
    );
  }

  const data = (await response.json()) as {
    content?: Array<{ type: string; text?: string }>;
  };
  const content = data.content?.find((b) => b.type === 'text')?.text ?? '';
  return extractHTML(content);
}

async function callGemini(config: AIConfig, userPrompt: string): Promise<string> {
  const model = config.model || 'gemini-1.5-pro';
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': config.apiKey,
    },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [{ parts: [{ text: userPrompt }] }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 4096,
      },
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `[generateWithAI] Gemini request failed (${response.status}): ${errorBody || response.statusText}`
    );
  }

  const data = (await response.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  };
  const text =
    data.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
  return extractHTML(text);
}

/**
 * Strip any markdown code fences the model may have added despite instructions.
 */
function extractHTML(raw: string): string {
  const trimmed = raw.trim();
  // Strip ```html ... ``` or ``` ... ```
  const fenceMatch = trimmed.match(/^```(?:html)?\s*\n?([\s\S]*?)\n?```\s*$/);
  if (fenceMatch) return fenceMatch[1].trim();
  return trimmed;
}
